import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { templateKeys } from './useTemplates';
import type { UpdateTemplateRequest, Template } from '../../lib/types/template.types';

/**
 * Update an existing template mutation
 * Updates detail cache and invalidates lists on success
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * const updateTemplate = useUpdateTemplate();
 * updateTemplate.mutate({
 *   id: 'template-id',
 *   data: { name: 'Updated Name' }
 * });
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTemplateRequest }) => {
      const { data: template } = await api.put<Template>(`/api/templates/${id}`, data);
      return template;
    },
    onSuccess: (updatedTemplate) => {
      // Update detail cache with new data
      queryClient.setQueryData(
        templateKeys.detail(updatedTemplate.id),
        updatedTemplate
      );
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    },
  });
}
